import { useLayoutEffect, useRef, useState, type ReactNode } from "react";

// Hand-built SVG charts for the dashboards (dataviz skill method):
// thin marks, 4px rounded data-ends on bars, 2px lines, >=8px markers with a
// 2px surface ring, 2px surface gaps between stacked segments, hairline
// recessive grid, one axis, legend for >=2 series, hover tooltips, and a
// text summary for screen readers. Series colors come from --dv-* tokens in
// dashboards.css (validated with scripts/validate_palette.js, light + dark).

export const SERIES = ["var(--dv-1)", "var(--dv-2)", "var(--dv-3)", "var(--dv-4)"];
export const OTHER = "var(--dv-other)";
export const seriesColor = (i: number) => (i < SERIES.length ? SERIES[i] : OTHER);

export function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [w, setW] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setW(el.clientWidth);
    const ro = new ResizeObserver(([e]) => setW(Math.round(e.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
}

export function niceMax(v: number) {
  if (v <= 0) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / p;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * p;
}
export function ticks(min: number, max: number, count = 4) {
  const step = (max - min) / count;
  return Array.from({ length: count + 1 }, (_, i) => min + step * i);
}
const fmt = (v: number) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}K` : Number.isInteger(v) ? String(v) : v.toFixed(1));

export function Legend({ items }: { items: { label: string; color: string }[] }) {
  if (items.length < 2) return null;
  return (
    <ul className="dv-legend" aria-label="Legend">
      {items.map((it) => <li key={it.label}><i style={{ background: it.color }} />{it.label}</li>)}
    </ul>
  );
}

function Tip({ x, y, w, children }: { x: number; y: number; w: number; children: ReactNode }) {
  const left = Math.min(Math.max(x, 90), w - 90);
  return <div className="dv-tip" style={{ left, top: y }} role="presentation">{children}</div>;
}

// ---- Stacked columns (e.g. visits per day by advisor) ----
export interface StackCol { key: string; label: string; values: number[] }
export function StackedColumns({ cols, series, height = 240, summary }: { cols: StackCol[]; series: { label: string; color: string }[]; height?: number; summary: string }) {
  const [ref, w] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const pad = { l: 34, r: 8, t: 12, b: 26 };
  const iw = Math.max(0, w - pad.l - pad.r), ih = height - pad.t - pad.b;
  // Counts: integer ticks only (0, step, 2·step…), 4 intervals.
  const raw = Math.max(1, ...cols.map((c) => c.values.reduce((a, b) => a + b, 0)));
  const step = Math.max(1, Math.ceil(raw / 4));
  const max = step * 4;
  const band = cols.length ? iw / cols.length : 0;
  const bw = Math.min(24, Math.max(6, band * 0.62));
  const y = (v: number) => pad.t + ih - (v / max) * ih;
  const every = Math.ceil(cols.length / Math.max(1, Math.floor(iw / 44)));
  return (
    <div className="dv" ref={ref}>
      <Legend items={series} />
      {w > 0 && (
        <svg width={w} height={height} role="img" aria-label={summary}>
          {[0, 1, 2, 3, 4].map((k) => k * step).map((t) => (
            <g key={t}><line className="dv-grid" x1={pad.l} x2={w - pad.r} y1={y(t)} y2={y(t)} /><text className="dv-axis" x={pad.l - 6} y={y(t) + 3} textAnchor="end">{fmt(t)}</text></g>
          ))}
          {cols.map((c, i) => {
            const cx = pad.l + band * i + band / 2;
            let acc = 0;
            const total = c.values.reduce((a, b) => a + b, 0);
            const segs = c.values.map((v, si) => ({ v, si })).filter((s) => s.v > 0);
            return (
              <g key={c.key} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
                <rect x={pad.l + band * i} y={pad.t} width={band} height={ih} fill="transparent" />
                {segs.map((s, k) => {
                  const y0 = y(acc), y1 = y(acc + s.v);
                  acc += s.v;
                  const top = k === segs.length - 1;
                  const h = Math.max(1, y0 - y1 - (top ? 0 : 2));
                  return top
                    ? <path key={s.si} d={roundTop(cx - bw / 2, y1, bw, y0 - y1, 4)} fill={series[s.si].color} opacity={hover === null || hover === i ? 1 : 0.45} />
                    : <rect key={s.si} x={cx - bw / 2} y={y1 + 2} width={bw} height={h} fill={series[s.si].color} opacity={hover === null || hover === i ? 1 : 0.45} />;
                })}
                {total === 0 && <line className="dv-zero" x1={cx - bw / 2} x2={cx + bw / 2} y1={y(0)} y2={y(0)} />}
                {i % every === 0 && <text className="dv-axis" x={cx} y={height - 8} textAnchor="middle">{c.label}</text>}
              </g>
            );
          })}
          <line className="dv-base" x1={pad.l} x2={w - pad.r} y1={y(0)} y2={y(0)} />
        </svg>
      )}
      {hover !== null && cols[hover] && (
        <Tip x={pad.l + band * hover + band / 2} y={8} w={w}>
          <div className="dv-tip-title">{cols[hover].label}</div>
          {series.map((s, si) => cols[hover].values[si] ? <div key={s.label} className="dv-tip-row"><i style={{ background: s.color }} />{s.label}<b>{cols[hover].values[si]}</b></div> : null)}
          <div className="dv-tip-row dv-tip-total">Total<b>{cols[hover].values.reduce((a, b) => a + b, 0)}</b></div>
        </Tip>
      )}
    </div>
  );
}
function roundTop(x: number, y: number, w: number, h: number, r: number) {
  const rr = Math.min(r, w / 2, h);
  return `M${x},${y + h} V${y + rr} Q${x},${y} ${x + rr},${y} H${x + w - rr} Q${x + w},${y} ${x + w},${y + rr} V${y + h} Z`;
}

// ---- Line chart with crosshair tooltip (metric over time) ----
export interface LinePoint { t: number; v: number; ref?: string }
export interface LineSeries { key: string; label: string; color: string; points: LinePoint[]; emphasis?: boolean }
export function LineChart({ series, height = 300, summary, unit = "", onPoint }: { series: LineSeries[]; height?: number; summary: string; unit?: string; onPoint?: (p: LinePoint, s: LineSeries) => void }) {
  const [ref, w] = useWidth<HTMLDivElement>();
  const [hx, setHx] = useState<number | null>(null);
  const pad = { l: 44, r: 16, t: 14, b: 28 };
  const all = series.flatMap((s) => s.points);
  const t0 = Math.min(...all.map((p) => p.t)), t1 = Math.max(...all.map((p) => p.t));
  const vmin = Math.min(...all.map((p) => p.v)), vmax = Math.max(...all.map((p) => p.v));
  // Round tick step (1/2/5 × 10^n) with the axis snapped to whole steps.
  const span = Math.max(vmax - vmin, Math.abs(vmax) * 0.1, 1e-6);
  const step = niceMax(span / 4) / (niceMax(span / 4) / (span / 4) > 1.6 ? 2 : 1);
  const lo = vmin >= 0 && vmin < span ? 0 : Math.floor(vmin / step) * step;
  const hi = Math.max(lo + step, Math.ceil(vmax / step) * step);
  const yTicks = Array.from({ length: Math.round((hi - lo) / step) + 1 }, (_, i) => lo + i * step);
  const iw = Math.max(0, w - pad.l - pad.r), ih = height - pad.t - pad.b;
  const x = (t: number) => pad.l + (t1 === t0 ? iw / 2 : ((t - t0) / (t1 - t0)) * iw);
  const y = (v: number) => pad.t + ih - ((v - lo) / (hi - lo || 1)) * ih;
  const days = (t1 - t0) / 86_400_000;
  const xt = ticks(t0, t1, Math.min(6, Math.max(2, Math.floor(iw / 110))));
  const near = hx === null ? [] : series.map((s) => ({ s, p: s.points.reduce<LinePoint | null>((b, p) => (!b || Math.abs(x(p.t) - hx) < Math.abs(x(b.t) - hx) ? p : b), null) })).filter((n) => n.p && Math.abs(x(n.p.t) - hx) < 40);
  if (!all.length) return null;
  return (
    <div className="dv" ref={ref}>
      <Legend items={series.map((s) => ({ label: s.label, color: s.color }))} />
      {w > 0 && (
        <svg width={w} height={height} role="img" aria-label={summary}
          onMouseMove={(e) => setHx(e.clientX - (e.currentTarget as SVGSVGElement).getBoundingClientRect().left)} onMouseLeave={() => setHx(null)}
          onClick={() => { const n = near[0]; if (n?.p && onPoint) onPoint(n.p, n.s); }}>
          {yTicks.map((t) => <g key={t}><line className="dv-grid" x1={pad.l} x2={w - pad.r} y1={y(t)} y2={y(t)} /><text className="dv-axis" x={pad.l - 6} y={y(t) + 3} textAnchor="end">{fmt(t)}</text></g>)}
          {xt.map((t) => <text key={t} className="dv-axis" x={x(t)} y={height - 8} textAnchor="middle">{new Date(t).toLocaleDateString(undefined, days > 200 ? { month: "short", year: "2-digit" } : { month: "short", day: "numeric" })}</text>)}
          {series.map((s) => {
            const pts = [...s.points].sort((a, b) => a.t - b.t);
            const d = pts.map((p, i) => `${i ? "L" : "M"}${x(p.t).toFixed(1)},${y(p.v).toFixed(1)}`).join(" ");
            return (
              <g key={s.key}>
                <path d={d} fill="none" stroke={s.color} strokeWidth={s.emphasis ? 2.5 : 2} strokeLinejoin="round" strokeLinecap="round" />
                {pts.length <= 60 && pts.map((p, i) => <circle key={i} className="dv-dot" cx={x(p.t)} cy={y(p.v)} r={4} fill={s.color} />)}
              </g>
            );
          })}
          {hx !== null && near.length > 0 && <line className="dv-cross" x1={x(near[0].p!.t)} x2={x(near[0].p!.t)} y1={pad.t} y2={pad.t + ih} />}
          {near.map((n) => <circle key={n.s.key} className="dv-dot dv-dot--hot" cx={x(n.p!.t)} cy={y(n.p!.v)} r={6} fill={n.s.color} />)}
        </svg>
      )}
      {near.length > 0 && (
        <Tip x={x(near[0].p!.t)} y={6} w={w}>
          <div className="dv-tip-title">{new Date(near[0].p!.t).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</div>
          {near.map((n) => <div key={n.s.key} className="dv-tip-row"><i style={{ background: n.s.color }} />{n.s.label}<b>{fmt(n.p!.v)}{unit ? ` ${unit}` : ""}</b></div>)}
          {onPoint && <div className="dv-tip-hint">Click to open the visit</div>}
        </Tip>
      )}
    </div>
  );
}

// ---- Sparkline (small multiples) ----
export function Sparkline({ points, color = "var(--dv-1)", height = 44 }: { points: LinePoint[]; color?: string; height?: number }) {
  const [ref, w] = useWidth<HTMLDivElement>();
  const pts = [...points].sort((a, b) => a.t - b.t);
  if (pts.length < 2) return <div ref={ref} className="dv-spark-empty" style={{ height }}>Not enough readings</div>;
  const t0 = pts[0].t, t1 = pts.at(-1)!.t, v0 = Math.min(...pts.map((p) => p.v)), v1 = Math.max(...pts.map((p) => p.v));
  const x = (t: number) => 4 + ((t - t0) / (t1 - t0 || 1)) * (w - 8);
  const y = (v: number) => 4 + (height - 8) - ((v - v0) / (v1 - v0 || 1)) * (height - 8);
  const last = pts.at(-1)!;
  return (
    <div ref={ref} className="dv-spark" aria-hidden="true">
      {w > 0 && (
        <svg width={w} height={height}>
          <path d={pts.map((p, i) => `${i ? "L" : "M"}${x(p.t)},${y(p.v)}`).join(" ")} fill="none" stroke="var(--dv-muted)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
          <circle className="dv-dot" cx={x(last.t)} cy={y(last.v)} r={4} fill={color} />
        </svg>
      )}
    </div>
  );
}
